import { Checkbox, FormControlLabel, Snackbar } from '@mui/material'
import { MiteneFormattedMessage } from 'components/atoms/MiteneFormattedMessage'
import { Button, Heading, Text } from 'components/design-system'
import { TextField } from 'components/design-system/composites/TextField'
import { LightBulbIcon } from 'components/design-system/primitives/Icons/LightBulbIcon'
import { useInitialState } from 'hooks/useInitialState'
import { FormEvent, useState } from 'react'
import { flushSync } from 'react-dom'

export const LoginArea = () => {
  const [recaptchaToken, setRecaptchaToken] = useState('')
  const initialState = useInitialState('login')
  if (initialState === null) {
    return null
  }

  const {
    actionUrl,
    authenticityToken,
    errorMessage,
    email,
    recaptchaEnterpriseSiteKey,
    recaptchaAction,
  } = initialState
  const snackbarOpen = !!errorMessage

  const handleOnSubmit = (e: FormEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    grecaptcha.enterprise
      .execute(recaptchaEnterpriseSiteKey, { action: recaptchaAction })
      .then((token: string) => {
        // 同期的なレンダリングで g-recaptcha-response に値を入れさせるために flushSync を使う
        // TODO: 並行レンダリング対応をする
        flushSync(() => setRecaptchaToken(token))
        form.submit()
      })
  }

  const helpHref = `https://help.family-album.com/hc/categories/360002520653`

  const onForgottenPasswordClick = () => {
    window.location.href = helpHref
  }

  return (
    <div className='container max-w-lg mx-auto text-center px-4'>
      <Heading level={2} color='primary' className='mt-4'>
        <MiteneFormattedMessage id='loginarea.title' />
      </Heading>
      <form className='mt-6' method='post' action={actionUrl} onSubmit={handleOnSubmit}>
        <input type='hidden' name='authenticity_token' value={authenticityToken} />
        <input type='hidden' name='g-recaptcha-response' value={recaptchaToken} />
        <div className='text-left'>
          <TextField
            label={
              <Text size='body' color='primary' bold={true} className='mb-2'>
                <MiteneFormattedMessage id='loginarea.emailAddressPlaceholder' />
              </Text>
            }
            fullWidth={true}
            type='email'
            required={true}
            defaultValue={email}
            id='email'
            name='email'
            autoComplete='email'
          />
          <TextField
            label={
              <Text size='body' color='primary' bold={true} className='mt-6 mb-2'>
                <MiteneFormattedMessage id='loginarea.passwordPlaceholder' />
              </Text>
            }
            fullWidth={true}
            type='password'
            required={true}
            id='password'
            name='password'
            autoComplete='current-password'
          />
          <Text size='body' color='primary' className='mt-4'>
            <MiteneFormattedMessage
              id='loginarea.forgottenPassword'
              values={{
                link: (
                  <Button
                    variant='link'
                    onClick={onForgottenPasswordClick}
                    color='secondary'
                    className='m-2'
                    type='button'
                  >
                    <MiteneFormattedMessage id='loginarea.forgottenPasswordLinkText' />
                  </Button>
                ),
              }}
            />
          </Text>
        </div>
        <Button variant='primary' color='secondary' type='submit' className='mt-10 size-full'>
          <MiteneFormattedMessage id='loginarea.loginButton' />
        </Button>
        <div className='mt-8'>
          <FormControlLabel
            control={<Checkbox color='secondary' />}
            label={
              <Text size='body' color='primary' className='inline-block'>
                <MiteneFormattedMessage id='loginarea.autoLoginCheckboxLabel' />
              </Text>
            }
            name='auto_extending'
            value='true'
          />
        </div>
      </form>

      <div className='text-left mt-10 mb-24 p-4 bg-mi-bg-surface-secondary rounded-lg'>
        <div className='flex justify-start'>
          <LightBulbIcon size={24} />
          <Text size='body' color='primary' bold={true}>
            <MiteneFormattedMessage id='loginarea.accountRequired.title' />
          </Text>
        </div>
        <Text size='body' color='primary' className='block mt-2'>
          <MiteneFormattedMessage id='loginarea.accountRequired' />
        </Text>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={snackbarOpen}
        ContentProps={{ 'aria-describedby': 'message-id' }}
        message={errorMessage}
      />
    </div>
  )
}
